import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import Guid from "_shared/guids/guid";
import axios, { AxiosHeaders, CanceledError, HttpStatusCode } from "axios";
import { InvalidRegistrationInfoError } from "legal-entities/registration-info/registration-info";
import EntityAgreement from "legal-entities/agreements/entity-agreement";
import { AdministratorInvitationNotFoundError } from "legal-entities/invitations/administrator-invitations/administrator-invitation";
import AcceptEntityUserInvitationAPIRequest from "legal-entities/invitations/member-invitations/email-based/_api/commands/accept-entity-user-invitation-api-command";
import EntityOfficerInvitation, {
  EntityOfficerInvitationNotFoundError,
  UnauthorizedEntityOfficerInvitationAccessError,
} from "legal-entities/invitations/officer-invitations/entity-officer-invitation";
import Session from "users/__session/session";
import { AuthInfo } from "users/auth/info/auth-info";
import InviteEntityOfficerAPIRequest from "users/super-users/_api/commands/invite-entity-officer-api-request";
import AuthInfoSchema from "users/auth/info/auth-info-schema";

export default class EntityOfficerInvitationAPIService {
  private readonly headers: AxiosHeaders = new AxiosHeaders();

  constructor(session: Readonly<Session>) {
    // Some calls in this service will be made by users without valid session auth tokens
    if (session.authToken?.value) {
      this.headers.set("Authorization", `Bearer ${session.authToken.value}`);
    }
  }

  async inviteEntityOfficer(invitation: EntityOfficerInvitation): Promise<EntityOfficerInvitation> {
    const url = new URL(`/super-user/invite-entity-officer`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.post(url.toString(), new InviteEntityOfficerAPIRequest(invitation), {
        headers: this.headers.concat({ "Content-Type": "application/json-patch+json" }),
      });
      const updatedInvitation = EntityOfficerInvitation.create({
        params: {
          ...invitation,
          id: new Guid(response.data),
        },
      });
      return updatedInvitation;
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      throw new EntityOfficerInvitationServiceError("inviteEntityOfficer", error, invitation);
    }
  }

  async getAllOpenOfficerInvitations(abortController?: AbortController): Promise<EntityOfficerInvitation[]> {
    const url = new URL(`/legal-entities/officer-invitations`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: abortController?.signal,
      });
      return EntityOfficerInvitation.fromJSONArray(response.data.invitations);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      throw new EntityOfficerInvitationServiceError("getAllOpenOfficerInvitations", error);
    }
  }

  async getOfficerInvitationById(id: Guid, abortController?: AbortController): Promise<EntityOfficerInvitation> {
    const url = new URL(`/legal-entities/officer-invitations/${id.value}`, AttorneyHubAPIService.apiBaseUrl);
    try {
      const response = await axios.get(url.toString(), {
        headers: this.headers,
        signal: abortController?.signal,
      });
      return EntityOfficerInvitation.fromJSON(response.data);
    } catch (error) {
      if (error instanceof CanceledError) throw error;
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) {
        throw new EntityOfficerInvitationNotFoundError(id);
      }
      throw new EntityOfficerInvitationServiceError("getOfficerInvitationById", error);
    }
  }

  async acceptOfficerInvitation(invitation: EntityOfficerInvitation, session: Session): Promise<EntityAgreement> {
    if (!invitation.id) throw new Error("Invitation ID is required to accept an invitation.");
    if (!invitation.registrationInfo?.password) throw new Error("Password is required to accept an invitation.");

    const url = new URL(
      `/legal-entities/officer-invitations/${invitation.id.value}/accept`,
      AttorneyHubAPIService.apiBaseUrl,
    );
    try {
      const response = await axios.post(
        url.toString(),
        new AcceptEntityUserInvitationAPIRequest(
          invitation.registrationInfo.password,
          invitation.registrationInfo.name,
        ),
        {
          headers: this.headers.concat({ "Content-Type": "application/json-patch+json" }),
        },
      );
      const authResponse = new AuthInfo(AuthInfoSchema.deserialize(response.data.authInfo)).toAuthResponse();
      session.initializeFromAuthResponse(authResponse);
      return EntityAgreement.fromJSON(response.data.entityAgreement);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest) {
        throw new InvalidRegistrationInfoError(error.response.data);
      }
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) {
        throw new AdministratorInvitationNotFoundError(invitation.id);
      }
      throw new EntityOfficerInvitationServiceError("acceptOfficerInvitation", error);
    }
  }

  async cancelEntityOfficerInvitation(invitation: EntityOfficerInvitation): Promise<void> {
    if (!invitation.id) throw new Error("Cannot cancel an invitation that has not been saved.");
    const url = new URL(
      `/legal-entities/officer-invitations/${invitation.id.value}/cancel`,
      AttorneyHubAPIService.apiBaseUrl,
    );
    try {
      await axios.post(
        url.toString(),
        {},
        {
          headers: this.headers,
        },
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) {
        throw new EntityOfficerInvitationNotFoundError(invitation.id);
      }
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Forbidden) {
        throw new UnauthorizedEntityOfficerInvitationAccessError();
      }
      throw new EntityOfficerInvitationServiceError("cancelEntityOfficerInvitation", error, invitation);
    }
  }
}

export class EntityOfficerInvitationServiceError extends Error {
  invitation?: EntityOfficerInvitation;
  method: string;
  error: unknown;
  constructor(method: string, error: unknown, invitation?: EntityOfficerInvitation) {
    super(`Error inviting entity officer: ${error instanceof Error ? error.message : String(error)}`);
    this.method = method;
    this.error = error;
    this.invitation = invitation;
  }
}
